import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useCart } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { ShoppingBag, User, Menu, X, ChevronDown, Globe } from 'lucide-react';
import { Button } from './ui/button';
import AuthModal from './AuthModal';
import CartSidebar from './CartSidebar';

function NavDropdown({ label, items, testId }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button 
        className="nav-link text-sm font-medium flex items-center gap-1 py-2"
        data-testid={testId}
        onClick={() => setIsOpen(!isOpen)}
      >
        {label}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute top-full left-0 pt-1">
          <div className="bg-white rounded-xl shadow-lg border border-[#C4A77D]/10 py-2 min-w-[180px] animate-fadeIn">
            {items.map((item, i) => (
              <Link
                key={i}
                to={item.href}
                className="block px-4 py-2 text-sm text-[#595959] hover:bg-[#F4EDE6] hover:text-[#C4A77D] transition-colors"
                data-testid={item.testId}
                onClick={() => setIsOpen(false)}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const { t, toggleLang } = useLanguage();
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [showCart, setShowCart] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [mobileDropdown, setMobileDropdown] = useState(null);

  const openLogin = () => {
    setAuthMode('login');
    setShowAuth(true);
  };

  const toggleMobileDropdown = (name) => {
    setMobileDropdown(mobileDropdown === name ? null : name);
  };

  return (
    <>
      <nav className="sticky top-0 z-40 bg-[#F4EDE6]/80 backdrop-blur-lg border-b border-[#C4A77D]/10" data-testid="navbar">
        <div className="container-main">
          <div className="flex items-center justify-between h-24">
            {/* Home Logo - Left */}
            <Link to="/" className="flex-shrink-0" data-testid="home-logo-link">
              <img 
                src="/vardagslyx-logo.png" 
                alt="Vardagslyx" 
                className="h-28 w-auto"
              />
            </Link>

            {/* Desktop Navigation - Centered */}
            <div className="hidden md:flex items-center gap-8 flex-1 justify-center">
              {/* Vad är Vardagslyx? - direct link */}
              <Link to="/vad-ar-vardagslyx" className="nav-link text-sm font-medium" data-testid="nav-about">
                {t('nav.about')}
              </Link>

              {/* Upplevelser - direct link */}
              <Link to="/upplevelser" className="nav-link text-sm font-medium" data-testid="nav-experiences">
                {t('nav.experiences')}
              </Link>

              {/* Vanliga Frågor with dropdown */}
              <NavDropdown 
                label={t('nav.faq')}
                testId="nav-faq"
                items={[
                  { label: t('nav.contact'), href: "/kontakt", testId: "nav-contact" },
                  { label: t('nav.customerService'), href: "/kundservice", testId: "nav-customer-service" }
                ]}
              />
            </div>

            {/* Right side: Bli Medlem, Logga in, Cart */}
            <div className="hidden md:flex items-center gap-4">
              <Link to="/bli-medlem" className="nav-link text-sm font-medium" data-testid="nav-membership">
                {t('nav.membership')}
              </Link>

              <Link to="/bli-partner" className="nav-link text-sm font-medium" data-testid="nav-partner">
                {t('nav.partner')}
              </Link>

              {user ? (
                <div className="flex items-center gap-2">
                  <Link to="/profil" className="nav-link text-sm font-medium flex items-center gap-1" data-testid="profile-link">
                    <User className="w-4 h-4" />
                    {user.name}
                  </Link>
                  <button
                    onClick={logout}
                    className="text-sm text-[#8C8C8C] hover:text-[#C4A77D] transition-colors"
                    data-testid="logout-button"
                  >
                    {t('nav.logout')}
                  </button>
                </div>
              ) : (
                <button 
                  onClick={openLogin} 
                  className="nav-link text-sm font-medium"
                  data-testid="login-button"
                >
                  {t('nav.login')}
                </button>
              )}

              <button
                onClick={() => setShowCart(true)}
                className="relative p-2 hover:bg-[#C4A77D]/5 rounded-full transition-colors"
                data-testid="cart-button"
              >
                <ShoppingBag className="w-5 h-5 text-[#C4A77D]" />
                {cart.items.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#C4A77D] text-white text-xs rounded-full flex items-center justify-center font-medium">
                    {cart.items.length}
                  </span>
                )}
              </button>

              <button
                onClick={toggleLang}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-[#C4A77D] hover:bg-[#C4A77D]/10 rounded-full transition-colors"
                data-testid="lang-toggle"
              >
                <Globe className="w-4 h-4" />
                {t('nav.lang')}
              </button>
            </div>

            {/* Mobile: Cart + Menu grouped right */}
            <div className="md:hidden flex items-center gap-1 ml-auto">
              <button
                onClick={() => setShowCart(true)}
                className="relative p-2 hover:bg-[#C4A77D]/5 rounded-full transition-colors"
                data-testid="cart-button-mobile"
              >
                <ShoppingBag className="w-5 h-5 text-[#C4A77D]" />
                {cart.items.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#C4A77D] text-white text-xs rounded-full flex items-center justify-center font-medium">
                    {cart.items.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setMobileMenu(!mobileMenu)}
                className="p-2 hover:bg-[#C4A77D]/5 rounded-full"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenu && (
            <div className="md:hidden py-4 border-t border-[#C4A77D]/10 animate-fadeIn">
              <div className="flex flex-col gap-1">
                {/* Vad är Vardagslyx? */}
                <Link 
                  to="/vad-ar-vardagslyx" 
                  className="py-3 px-2 text-[#1A1A1A] font-medium"
                  onClick={() => setMobileMenu(false)}
                >
                  {t('nav.about')}
                </Link>

                {/* Upplevelser */}
                <Link 
                  to="/upplevelser" 
                  className="py-3 px-2 text-[#1A1A1A] font-medium"
                  onClick={() => setMobileMenu(false)}
                >
                  {t('nav.experiences')}
                </Link>

                {/* Bli Medlem */}
                <Link 
                  to="/bli-medlem" 
                  className="py-3 px-2 text-[#1A1A1A] font-medium"
                  onClick={() => setMobileMenu(false)}
                >
                  {t('nav.membership')}
                </Link>

                {/* Bli Partner */}
                <Link 
                  to="/bli-partner" 
                  className="py-3 px-2 text-[#1A1A1A] font-medium"
                  onClick={() => setMobileMenu(false)}
                >
                  {t('nav.partner')}
                </Link>

                {/* Logga in */}
                {user ? (
                  <>
                    <Link 
                      to="/profil" 
                      className="py-3 px-2 text-[#1A1A1A] font-medium"
                      onClick={() => setMobileMenu(false)}
                    >
                      {user.name}
                    </Link>
                    <button 
                      onClick={() => { logout(); setMobileMenu(false); }} 
                      className="py-3 px-2 text-left text-[#B83A3A] font-medium"
                    >
                      {t('nav.logout')}
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={() => { openLogin(); setMobileMenu(false); }}
                    className="py-3 px-2 text-left text-[#1A1A1A] font-medium"
                  >
                    {t('nav.login')}
                  </button>
                )}

                {/* Vanliga Frågor */}
                <div>
                  <button 
                    onClick={() => toggleMobileDropdown('faq')}
                    className="w-full py-3 px-2 text-left text-[#1A1A1A] font-medium flex items-center justify-between"
                  >
                    {t('nav.faq')}
                    <ChevronDown className={`w-4 h-4 transition-transform ${mobileDropdown === 'faq' ? 'rotate-180' : ''}`} />
                  </button>
                  {mobileDropdown === 'faq' && (
                    <>
                      <Link 
                        to="/kontakt" 
                        className="block py-2 px-6 text-[#595959]"
                        onClick={() => setMobileMenu(false)}
                      >
                        {t('nav.contact')}
                      </Link>
                      <Link 
                        to="/kundservice" 
                        className="block py-2 px-6 text-[#595959]"
                        onClick={() => setMobileMenu(false)}
                      >
                        {t('nav.customerService')}
                      </Link>
                    </>
                  )}
                </div>

                {/* Language toggle */}
                <button
                  onClick={toggleLang}
                  className="py-3 px-2 text-left text-[#C4A77D] font-medium flex items-center gap-2"
                >
                  <Globe className="w-4 h-4" />
                  {t('nav.lang')}
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} mode={authMode} setMode={setAuthMode} />
      <CartSidebar isOpen={showCart} onClose={() => setShowCart(false)} />
    </>
  );
}
